import React from 'react';
import config from '../config';
import socketIOClient from 'socket.io-client';
import {
  disconnectAndSetup,
  setCurrentRemoteUser,
  setEndReason,
  setIncomingCallState,
  stopRinging,
  wasRecentlyAnswered,
} from './webrtc.duck';
import { setOnlineStatus } from './socketChat.duck';
import { brandedConfirmAlert } from '../components/PopupShell/brandedConfirmAlert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhoneSlash } from '@fortawesome/free-solid-svg-icons';
// import ringtoneMp3 from '../assets/sounds/ringing_phone.mp3';
// ================ Action types ================ //

export const SOCKET_SUCCESS = 'app/CoachSaaS/SOCKET_SUCCESS';
export const SET_USER_STATUS = 'app/CoachSaaS/SET_USER_STATUS';

// ================ Reducer ================ //

const initialState = {
  socket: null,
  userStatus: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SOCKET_SUCCESS:
      return {
        ...state,
        socket: payload,
      };

    case SET_USER_STATUS:
      const { id, status } = payload;
      state.userStatus = {
        ...state.userStatus,
        [id]: {
          status: status,
        },
      };
      // state.userStatus[id] = status;
      return {
        ...state,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const socketSuccess = socket => ({ type: SOCKET_SUCCESS, payload: socket });
export const setUserStatus = payload => ({ type: SET_USER_STATUS, payload: payload });

// ================ Thunks ================ //

// requestSocket() is called independently from several mount points on the
// same page (PageWrapper, the active-session pages, SideChat) — they can all
// fire within the same tick on page load. The staleness check below reads
// Redux state, which only updates once the FIRST caller's connection
// finishes; any caller that runs before that dispatch lands still sees
// `socket` as null and would otherwise open its OWN separate socket.io
// connection. That left the server with multiple live sockets registered
// under the same user uuid — offers could get routed to an orphaned one
// nothing was listening on, so calls hung on "waiting for the other
// participant" even though signaling worked in general. This in-flight
// promise makes concurrent callers share the same connection attempt.
let inFlightSocketPromise = null;

export const requestSocket = () => async (dispatch, getState, sdk) => {
  const { socket } = getState().socket;
  const { currentUser } = getState().user;

  if (inFlightSocketPromise) {
    return inFlightSocketPromise;
  }

  if ((!socket || (socket && socket.disconnected)) && currentUser) {
    inFlightSocketPromise = (async () => {
    console.log('socket connecting', currentUser.id.uuid);
    const socketRes = await socketIOClient(config.socketService, {
      query: `uuid=${currentUser.id.uuid}`,
    });

    dispatch(socketSuccess(socketRes));

    // 'offer' is now a lightweight call-invite ("ring the other participant")
    // rather than an SDP offer — the actual media connection is each side
    // independently joining the same LiveKit room (see webrtc.duck.js
    // offer()/answer()). We just show the incoming-call popup here.
    //
    // The caller retries this same invite every few seconds until answered
    // (webrtc.duck.js's scheduleInviteRetry — papers over a dropped/slow
    // signaling reconnect). If one of those retries lands AFTER we've
    // already answered or are already mid-call with that same caller, it
    // must be ignored — otherwise the callee sees the incoming-call popup
    // ("ringing") reappear on top of a call they already joined.
    if (!socketRes._callbacks['$offer']) {
      socketRes.on('offer', (invitePayload, userName, userId) => {
        console.log('inOffer', {userId, userName});
        // The call itself now happens in the separate Viveka Meet app (see
        // webrtc.duck.js) -- we can no longer tell "already connected" from
        // in-app Room/drawer state, since none exists here anymore. A retry
        // arriving after we've already answered this same caller is ignored
        // via a short cooldown instead.
        if (wasRecentlyAnswered(userId)) {
          return;
        }
        dispatch(setCurrentRemoteUser({ name: userName || 'N/A', id: userId }));
        dispatch(setIncomingCallState({ name: userName, id: userId }));
      });
    }

    // 'answer-call' now just tells the caller the callee picked up — each
    // side already independently joined the LiveKit room, so there's no SDP
    // to apply. Stopping the invite-retry loop here is the important bit.
    if (!socketRes._callbacks['$answer']) {
      socketRes.on('answer-call', (payload, userName, userId) => {
        console.log('inAnswer', {userId, userName});
        stopRinging(userId);
        dispatch(setCurrentRemoteUser({ name: userName || 'N/A', id: userId }));
      });
    }

    if (!socketRes._callbacks['$user-status']) {
      socketRes.on('user-status', (userId, isUserAvailable) => {
        dispatch(setUserStatus({ id: userId, status: isUserAvailable }));
      });
    }

    if (!socketRes._callbacks['$reject-call']) {
      socketRes.on('reject-call', () => {
        // Tag the telemetry end reason BEFORE dispatching disconnect so the
        // telemetry summary records "the peer rejected" rather than the
        // generic 'self-hangup' default.
        setEndReason('peer-rejected');
        dispatch(disconnectAndSetup());
        brandedConfirmAlert({
          title: 'Call was rejected',
          message: "They're not available right now. Try again later.",
          tone: 'danger',
          icon: <FontAwesomeIcon icon={faPhoneSlash} size="lg" color="#fff" />,
          buttons: [
            {
              label: 'Ok',
            },
          ],
        });
      });
    }

    if (!socketRes._callbacks['$disconnect-call']) {
      socketRes.on('disconnect-call', () => {
        const { currentRemoteUser } = getState().webrtc;
        let messageTitle;
        if(currentRemoteUser){
          messageTitle = 'Call was disconnected by ' + currentRemoteUser.name;
        }else {
          messageTitle = 'Call was disconnected';
        }
        setEndReason('peer-hangup');
        dispatch(disconnectAndSetup());
        brandedConfirmAlert({
          title: messageTitle,
          message: 'You can start a new call anytime.',
          tone: 'danger',
          icon: <FontAwesomeIcon icon={faPhoneSlash} size="lg" color="#fff" />,
          buttons: [
            {
              label: 'Ok',
            },
          ],
        });
      });
    }

    if (!socketRes.connected) {
      // socketIOClient() returns immediately; the socket is still completing
      // its handshake. Resolve only once it is actually connected so the
      // server has registered this user's uuid->socket mapping BEFORE the
      // caller emits offer/answer. Without this the first call after
      // page-load routed signaling to an unregistered socket and silently
      // failed until a manual retry/refresh.
      await new Promise(resolve => {
        let settled = false;
        const finish = () => {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        socketRes.once('connect', finish);
        // Safety net: never block the call UI indefinitely. socket.io buffers
        // emits until 'connect', so proceeding after the timeout is still safe.
        setTimeout(finish, 8000);
      });
    }

    return socketRes;
    })();

    try {
      return await inFlightSocketPromise;
    } finally {
      inFlightSocketPromise = null;
    }
  } else {
    return socket;
  }
  // socket.emit('join', transactionId);
};
